import React, { memo, useEffect, useState } from "react";
import { Link } from "gatsby";
import Eye from "../icons/Eye";
import ThemeSwitcher from "./ThemeSwitcher";
import useLavaLampIndicator from "../hooks/useLavaLampIndicator";

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHeroVisible, setIsHeroVisible] = useState(false);
  const { containerRef: mainNavRef, lavaLampHandlers } =
    useLavaLampIndicator<HTMLElement>({
      activeSelector: "a.active",
      itemSelector: "a",
    });

  useEffect(() => {
    let pendingFrame = 0;
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 56);

      const projectHeroColor = document.querySelector<HTMLElement>(
        ".project-hero-color"
      );
      if (!projectHeroColor) {
        setIsHeroVisible(false);
        return;
      }

      const navRect = document
        .querySelector<HTMLElement>(".main-nav")
        ?.getBoundingClientRect();
      const navTop = navRect?.top ?? 0;
      const heroBottom = projectHeroColor.getBoundingClientRect().bottom;

      setIsHeroVisible(heroBottom > navTop);
    };

    const scheduleScrollUpdate = () => {
      if (pendingFrame) return;
      pendingFrame = window.requestAnimationFrame(() => {
        pendingFrame = 0;
        handleScroll();
      });
    };
    handleScroll();
    window.addEventListener("scroll", scheduleScrollUpdate, { passive: true });
    window.addEventListener("ux-eye-candy:scroll-top", handleScroll);
    return () => {
      window.cancelAnimationFrame(pendingFrame);
      window.removeEventListener("scroll", scheduleScrollUpdate);
      window.removeEventListener("ux-eye-candy:scroll-top", handleScroll);
    };
  }, []);

  return (
    <header
      className="site-header app-header"
      data-scrolled={isScrolled}
      data-hero-visible={isHeroVisible}
      aria-label="Site header"
    >
      <div className="header-logo-wrap">
        <Link className="header-logo" to="/" aria-label="UX and Eye Candy home">
          <Eye aria-hidden="true" />
        </Link>
      </div>
      <div className="main-nav-wrap">
        <nav
          className="main-nav"
          aria-label="Primary navigation"
          ref={mainNavRef}
          {...lavaLampHandlers}
        >
          <span className="lava-lamp-indicator" aria-hidden="true" />
          <Link to="/work/" activeClassName="active" partiallyActive>
            Work
          </Link>
          <Link to="/process/" activeClassName="active" partiallyActive>
            Process
          </Link>
          <Link to="/about/" activeClassName="active" partiallyActive>
            About
          </Link>
        </nav>
      </div>
      <div className="header-theme-wrap">
        <ThemeSwitcher />
      </div>
    </header>
  );
}

export default memo(Header);
